import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const CartContext = createContext(null);

const loadCart = () => {
  try {
    return JSON.parse(localStorage.getItem('glowos_cart') || '[]');
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadCart);

  const persist = (next) => {
    setItems(next);
    localStorage.setItem('glowos_cart', JSON.stringify(next));
  };

  const addItem = useCallback((product, quantity = 1, branchId) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.productId === product._id);
      let next;
      if (idx >= 0) {
        next = prev.map((i, n) =>
          n === idx ? { ...i, quantity: i.quantity + quantity } : i
        );
      } else {
        const campaign = product.campaignPricing;
        const finalPrice = Number(campaign?.finalPrice ?? product.price ?? 0);
        next = [
          ...prev,
          {
            productId: product._id,
            tenantId: product.tenantId?._id || product.tenantId,
            branchId: branchId || product.branchId || null,
            name: product.name,
            price: finalPrice,
            basePrice: Number(campaign?.basePrice ?? product.price ?? 0),
            campaignName: campaign?.name || '',
            image: product.images?.[0]?.url,
            salonName: product.salonName,
            quantity,
            stock: product.stock,
          },
        ];
      }
      localStorage.setItem('glowos_cart', JSON.stringify(next));
      return next;
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    setItems((prev) => {
      const next = quantity <= 0
        ? prev.filter((i) => i.productId !== productId)
        : prev.map((i) => (i.productId === productId ? { ...i, quantity } : i));
      localStorage.setItem('glowos_cart', JSON.stringify(next));
      return next;
    });
  }, []);

  const clearCart = useCallback(() => persist([]), []);

  const total = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      itemCount: items.reduce((s, i) => s + i.quantity, 0),
      total,
      addItem,
      updateQuantity,
      clearCart,
    }),
    [items, total, addItem, updateQuantity, clearCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
